import { Link } from 'react-router-dom';
import { useMyListings } from '../hooks/useListings.js';
import { TRAILER_TYPE_MAP } from '../config/trailerTypes.js';
import type { ListingStatus } from '@shared/api-types.js';

export function AccountListingsPage() {
  const { data, isLoading } = useMyListings();

  return (
    <div className="mx-auto max-w-5xl px-4 py-12">
      <div className="flex items-center justify-between mb-8">
        <div>
          <Link to="/account" className="text-sm text-navy-500 hover:text-navy">
            ← Back to account
          </Link>
          <h1 className="text-3xl font-bold text-navy mt-2">My listings</h1>
        </div>
        <Link to="/listings/new" className="btn-primary">
          Add a trailer
        </Link>
      </div>

      {isLoading && <p className="text-navy-500">Loading…</p>}
      {data?.listings.length === 0 && (
        <div className="card p-12 text-center">
          <p className="text-navy-600 mb-4">You haven't listed any trailers yet.</p>
          <Link to="/listings/new" className="btn-primary">
            List your first trailer
          </Link>
        </div>
      )}

      <div className="space-y-3">
        {data?.listings.map((l) => (
          <Link
            key={l.id}
            to={`/listings/${l.id}/edit`}
            className="card p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
          >
            <div className="w-20 h-20 rounded-lg bg-navy-50 overflow-hidden flex-shrink-0">
              {l.photos[0] ? (
                <img
                  src={l.photos[0].thumbnailUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-2xl">
                  {TRAILER_TYPE_MAP[l.trailerType]?.emoji ?? '🛻'}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-navy truncate">{l.title}</div>
              <div className="text-sm text-navy-500 truncate">
                {TRAILER_TYPE_MAP[l.trailerType]?.label} · {l.city}, {l.province}
              </div>
              <div className="text-sm text-navy-700 mt-1">
                ${(l.dailyRateCents / 100).toFixed(0)} / day
              </div>
            </div>
            <StatusBadge status={l.status} />
          </Link>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: ListingStatus }) {
  const map: Record<ListingStatus, { label: string; cls: string }> = {
    draft: { label: 'Draft', cls: 'bg-navy-100 text-navy-700' },
    pending_review: { label: 'In review', cls: 'bg-orange-100 text-orange-700' },
    active: { label: 'Active', cls: 'bg-green-100 text-green-700' },
    paused: { label: 'Paused', cls: 'bg-navy-100 text-navy-600' },
    banned: { label: 'Banned', cls: 'bg-red-100 text-red-700' },
  };
  const s = map[status];
  return (
    <span
      className={`text-xs font-semibold uppercase tracking-wider px-2 py-1 rounded ${s.cls}`}
    >
      {s.label}
    </span>
  );
}
